import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import DateField from '../../components/Fields/DateField';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import NumberField from '../../components/Fields/NumberField';
import SelectListField from '../../components/Fields/SelectListField';
import SwitchField from '../../components/Fields/SwitchField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { Result } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';

interface ItemStatusEditModel {
    id: number

    name: string
    itemStatusTypeId: number
    canCheckOut: boolean
    color: string
    beginDate: string
    endDate: string

    concurrencyId?: string
}

const ItemStatusView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<ItemStatusEditModel>({
        id: 0,
        name: '',
        itemStatusTypeId: 0,
        color: '',
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
        canCheckOut: false,
        concurrencyId: '',
    });

    const itemStatusTypes = useSelectListOptions('api/v1/selectlist/ItemStatusTypes?mainTypesOnly=false', '-- Select status type');

    let idParams: IdParams = useParams();
    let itemStatusId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        itemStatusId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (itemStatusId !== undefined) {
            DataService.getById<ItemStatusEditModel>('api/v1/itemstatuses/', itemStatusId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [itemStatusId]);

    function submit() {
        if (formData) {
            if (formData.id === 0)
                DataService
                    .create<ItemStatusEditModel>(`api/v1/itemstatuses/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
            else
                DataService
                    .update<ItemStatusEditModel>(`api/v1/itemstatuses/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'itemStatusTypeId', label: 'Itemstatus type', validationRules: [{ type: 'isRequired' }] },
            { name: 'color', label: 'Color' },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
            { name: 'canCheckOut', label: 'Can Check-out' },
            { name: 'sortOrder', label: 'Sort Order' },

        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (itemStatusId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.EditItemStatus)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Itemstatus' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.EditItemStatus}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('name')} />
                            <SelectListField {...getFieldProps('itemStatusTypeId')} options={itemStatusTypes} />
                            <TextboxField {...getFieldProps('color')} />
                            <NumberField {...getFieldProps('sortOrder')} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                            <SwitchField {...getFieldProps('canCheckOut')} />
                        </Col>
                    </Row>
                </Container>
            </Form>
        </div>
    );
}
export default ItemStatusView;